import React, { useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  getEntitiesByDomain,
  getMitigatingControls,
  getCounteringDefenses,
  isKevExploited,
} from '../../lib/selectors';
import KevBadge from './KevBadge';

// Coverage / gap analysis (build-brief §7.2). Per domain, which techniques have no
// mapped control — prioritised by active exploitation (KEV). Threat rows carry a
// severity dot (oxblood/purple), a coverage count, or an oxblood "gap" tag.
const CoverageView = ({ domainId, onSelectTechnique }) => {
  const [kevOnly, setKevOnly] = useState(false);

  const rows = useMemo(() => {
    const techniques = getEntitiesByDomain(domainId, 'offensive-technique');
    const computed = techniques.map((t) => {
      const controls = getMitigatingControls(t.id).length;
      const countermeasures = getCounteringDefenses(t.id).length;
      const kev = isKevExploited(t.id);
      const gap = controls === 0;
      return { id: t.id, name: t.name, controls, countermeasures, kev, gap };
    });
    const priority = (r) => (r.kev && r.gap ? 0 : r.gap ? 1 : r.kev ? 2 : 3);
    return computed.sort(
      (a, b) => priority(a) - priority(b) || a.controls - b.controls || a.id.localeCompare(b.id),
    );
  }, [domainId]);

  const visible = kevOnly ? rows.filter((r) => r.kev) : rows;
  const gapCount = rows.filter((r) => r.gap).length;
  const kevGapCount = rows.filter((r) => r.kev && r.gap).length;

  if (rows.length === 0) {
    return (
      <div className="tv-panel">
        <p className="tv-empty">No ATT&CK techniques are mapped to this pillar yet.</p>
      </div>
    );
  }

  return (
    <div className="tv-panel">
      <h2>
        <span className="tv-ph-dot" style={{ background: 'var(--tv-threat)' }} />
        Coverage &amp; Gaps
      </h2>
      <p className="tv-hint" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <span>
          <span style={{ color: 'var(--tv-threat)' }}>{gapCount}</span> unmitigated ·{' '}
          <span style={{ color: 'var(--tv-threat)' }}>{kevGapCount}</span> KEV gaps ·{' '}
          <span style={{ color: 'var(--tv-text)' }}>{rows.length}</span> techniques
        </span>
        <button
          className="tv-btn"
          style={kevOnly ? { borderColor: 'var(--tv-threat)', color: 'var(--tv-threat)' } : undefined}
          onClick={() => setKevOnly((v) => !v)}
        >
          <AlertTriangle style={{ width: 13, height: 13 }} /> KEV only
        </button>
      </p>

      {visible.map((r) => {
        const sev = r.kev && r.gap ? 'crit' : r.kev ? 'high' : r.gap ? 'med' : 'low';
        return (
          <button
            key={r.id}
            className="tv-row thr"
            onClick={() => onSelectTechnique && onSelectTechnique(r.id)}
          >
            <span className={`tv-sev ${sev}`} />
            <span style={{ flex: 1 }}>
              <span className="tv-rid">{r.id}</span>
              <div className="tv-rn">{r.name}</div>
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
              {r.kev && <KevBadge compact />}
              {r.gap ? (
                <span className="tv-gap-tag">gap</span>
              ) : (
                <span className="tv-rmeta" style={{ marginTop: 0 }}>{r.controls} controls</span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default CoverageView;
